import React, { useState, useCallback } from 'react';
import ErrorImg from '../images/load_err.png';
import { supportsNativeLazy, removeAllUrlParams, hasUrlParams } from '../util';
import { useLazyLoadImg } from '../hooks/useLazyLoadImg';
import { useUpdateEffect } from 'ahooks';

export interface LazyImgProps extends React.HTMLAttributes<HTMLImageElement> {
    src: string;
    webpSrc?: string;
    // 加载失败占位图
    errorSrc?: string | null;
    alt?: string;
    className?: string;
    // 占位高度
    intrinsicHeight?: string;
    lazyOptions?: IntersectionObserverInit;
}

enum E_LOAD_STATUS {
    LOADING,
    SUCCESS,
    ERROR,
}

const loadStatusMap = {
    [E_LOAD_STATUS.LOADING]: 'lazy-loading',
    [E_LOAD_STATUS.SUCCESS]: 'lazy-loaded',
    [E_LOAD_STATUS.ERROR]: 'lazy-error',
};

/**
 * 懒加载图片组件
 */
export const LazyImg = (props: LazyImgProps) => {
    const { webpSrc, errorSrc = ErrorImg, alt, src, lazyOptions, intrinsicHeight, onLoad, onError } = props;

    const [loadStatus, setLoadStatus] = useState<E_LOAD_STATUS>(E_LOAD_STATUS.LOADING);
    const [currentSrc, setCurrentSrc] = useState<string>(src);
    const [currentWebpSrc, setCurrentWebpSrc] = useState<string | undefined>(webpSrc);

    const lazyLoadImg = useLazyLoadImg(currentSrc, lazyOptions);

    useUpdateEffect(() => {
        setCurrentSrc(src);
        setCurrentWebpSrc(webpSrc);
        setLoadStatus(E_LOAD_STATUS.LOADING);
    }, [src, webpSrc]);

    const className = `lazy-img ${loadStatusMap[loadStatus]} ${props.className || ''}`;

    // 处理图片重试逻辑
    const handleRetry = useCallback(() => {
        // 如果URL没有参数，则不重试
        if (!hasUrlParams(currentSrc) && !currentWebpSrc) {
            return false;
        }

        // 移除所有参数后重试
        const urlWithoutParams = removeAllUrlParams(currentSrc);
        setCurrentWebpSrc(void 0);
        setCurrentSrc(urlWithoutParams);
        setLoadStatus(E_LOAD_STATUS.LOADING);
        return true;
    }, [currentSrc, currentWebpSrc]);

    const commonAttrs = {
        alt,
        className,
        onLoad(e: React.SyntheticEvent<HTMLImageElement, Event>) {
            setLoadStatus(E_LOAD_STATUS.SUCCESS);
            onLoad?.(e);
        },
        onError(e: React.SyntheticEvent<HTMLImageElement, Event>) {
            // 尝试重试
            const retried = handleRetry();

            // 如果没有重试，则设置为错误状态
            if (!retried) {
                setLoadStatus(E_LOAD_STATUS.ERROR);
                onError?.(e);
            }
        },
    };

    let Img = (
        <>
            {currentWebpSrc ? <source srcSet={currentWebpSrc} type="image/webp" /> : null}
            {supportsNativeLazy ? (
                <img {...commonAttrs} src={currentSrc} loading="lazy" />
            ) : (
                <img {...commonAttrs} data-src={currentSrc} ref={lazyLoadImg} />
            )}
        </>
    );

    if (errorSrc && loadStatus === E_LOAD_STATUS.ERROR) {
        Img = <img className={className} src={errorSrc} />;
    }

    const applyIntrinsicHeight = loadStatus === E_LOAD_STATUS.LOADING && intrinsicHeight;

    return <picture style={applyIntrinsicHeight ? { minHeight: intrinsicHeight } : void 0}>{Img}</picture>;
};
